import { forwardRef } from 'react';
import type { PropsWithoutRef, ReactNode, Ref } from 'react';

import type { ForwardRefForwardPropsComponent, TestIdProp, WithAttributes } from '../../types';
import useTestIds from '../../hooks/useTestIds';
import useI18n from '../../hooks/useI18n';
import Icon, { registerIcon } from '../Icon';
import * as pencilIcon from '../Icon/icons/pencil.icon';
import Flex from '../Flex';
import { useConsolidatedRef, useElement } from '../../hooks';
import type { FormDialogProps } from '../Dialog';

import { getInlineEditTestIds } from './InlineEdit.test-ids';
import type { elements } from './InlineEdit.test-ids';
import {
  StyledEditButton,
  StyledInlineEdit,
  StyledContent,
  StyledInlineEditDialog
} from './InlineEdit.styles';

registerIcon(pencilIcon);

export type InlineEditProps = WithAttributes<
  'div',
  TestIdProp<typeof elements> &
    Pick<FormDialogProps, 'progress'> & {
      /** Editable content. */
      children: ReactNode;
      /** Accessible label for the editable content. */
      contextualLabel: string;
      /**
       * Form content for updating the editable content rendered within a dialog.
       * Pass this value to show the dialog and conversely set to undefined to hide.
       */
      editForm?: ReactNode;
      /** Called when the edit button is clicked. */
      onEdit: () => void;
      /** Called when the user cancels the edit. */
      onCancel: () => void;
      /** Called when the user confirms the edit. */
      onSubmit: () => void;
      /** Ref forwarded to the wrapping element. */
      ref?: Ref<HTMLDivElement>;
    }
>;

const InlineEdit: ForwardRefForwardPropsComponent<InlineEditProps> = forwardRef(function InlineEdit(
  {
    children,
    contextualLabel,
    editForm,
    progress,
    onEdit,
    onCancel,
    onSubmit,
    testId,
    ...restProps
  }: PropsWithoutRef<InlineEditProps>,
  refProp: InlineEditProps['ref']
) {
  const ref = useConsolidatedRef(refProp);
  const t = useI18n();
  const testIds = useTestIds(testId, getInlineEditTestIds);
  const editLabel = `${t('edit')} - ${contextualLabel}`;
  const [editButton, setButtonEl] = useElement<HTMLButtonElement>();

  return (
    <Flex
      data-testid={testIds.root}
      {...restProps}
      container={{ inline: true, alignItems: 'start', justify: 'between', gap: 0.5 }}
      as={StyledInlineEdit}
      onDoubleClick={onEdit}
      ref={ref}
    >
      <StyledContent>{children}</StyledContent>

      <StyledEditButton
        data-testid={testIds.button}
        ref={setButtonEl}
        icon
        compact
        variant='simple'
        onClick={onEdit}
        label={t('edit')}
        aria-label={editLabel}
        aria-haspopup='dialog'
        aria-hidden={!!editForm && !!editButton ? true : undefined}
      >
        <Icon name='pencil' />
      </StyledEditButton>
      {editForm && editButton && (
        <StyledInlineEditDialog
          data-testid={testIds.dialog}
          progress={progress}
          target={editButton}
          placement='bottom-start'
          aria-label={editLabel}
          onCancel={onCancel}
          onSubmit={onSubmit}
        >
          {editForm}
        </StyledInlineEditDialog>
      )}
    </Flex>
  );
});

export default InlineEdit;
